import { useEffect, useId, useRef, useState, type CSSProperties } from 'react'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import Link from 'src/components/Link'
import AiGeneratedBadge from 'src/components/AiGeneratedBadge'
import styles from './HomeBrandCarousel.module.css'

export interface HomeBrandItem { label: string; href: string; imageSrc?: string; imageAlt?: string; aiGenerated?: boolean }

/** Native, manually controlled scrolling: no carousel timer or automatic motion. */
export default function HomeBrandCarousel({ items, title = 'Marken, die dein Zuhause besonders machen.', style }: {
  items: HomeBrandItem[]; title?: string; style?: CSSProperties
}) {
  const id = useId()
  const rail = useRef<HTMLUListElement>(null)
  const [canScroll, setCanScroll] = useState({ back: false, forward: false })
  const update = () => {
    const node = rail.current
    if (node) setCanScroll({ back: node.scrollLeft > 1, forward: node.scrollWidth - node.clientWidth - node.scrollLeft > 1 })
  }
  useEffect(() => {
    update()
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update)
    if (rail.current) observer?.observe(rail.current)
    window.addEventListener('resize', update)
    return () => { observer?.disconnect(); window.removeEventListener('resize', update) }
  }, [items.length])
  const scroll = (direction: -1 | 1 | 'start' | 'end') => {
    const node = rail.current
    if (!node) return
    const maximum = Math.max(0, node.scrollWidth - node.clientWidth)
    const current = Math.min(maximum, Math.max(0, node.scrollLeft))
    const left = direction === 'start' ? 0 : direction === 'end' ? maximum
      : Math.min(maximum, Math.max(0, current + direction * Math.max(180, node.clientWidth * .8)))
    // Target only this rail, including at its ends; never scroll an ancestor.
    node.scrollTo({ left, behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' })
  }
  if (!items.length) return null
  return <section className={styles.section} style={style} aria-labelledby={`${id}-title`} data-home-brands>
    <div className={styles.inner}>
      <div className={styles.heading}>
        <div><p className={styles.eyebrow}>Unsere Markenwelt</p><h2 id={`${id}-title`}>{title}</h2></div>
        <div className={styles.actions}>
          <Link href='/brands'>Alle Marken entdecken <span aria-hidden='true'>↗</span></Link>
          <div className={styles.controls}>
            <button type='button' aria-label='Vorherige Marken anzeigen' aria-controls={`${id}-brands`} disabled={!canScroll.back} onClick={() => scroll(-1)}><ArrowBackIcon /></button>
            <button type='button' aria-label='Weitere Marken anzeigen' aria-controls={`${id}-brands`} disabled={!canScroll.forward} onClick={() => scroll(1)}><ArrowForwardIcon /></button>
          </div>
        </div>
      </div>
      <ul ref={rail} id={`${id}-brands`} className={styles.rail} onScroll={update} aria-label='Markenauswahl' tabIndex={0}
        onKeyDown={event => {
          if (event.target !== event.currentTarget) return
          const direction = event.key === 'ArrowLeft' ? -1 : event.key === 'ArrowRight' ? 1
            : event.key === 'Home' ? 'start' : event.key === 'End' ? 'end' : null
          if (direction === null) return
          event.preventDefault()
          scroll(direction)
        }}>
        {items.map(item => <li key={`${item.href}-${item.label}`}>
          <Link className={styles.tile} href={item.href} aria-label={`${item.label} entdecken`}>
            {item.imageSrc ? <img src={item.imageSrc} alt={item.imageAlt || item.label} width={132} height={65} loading='lazy' /> : <span>{item.label}</span>}
            <AiGeneratedBadge aiGenerated={item.aiGenerated} size='compact' placement='top-left' />
          </Link>
        </li>)}
      </ul>
    </div>
  </section>
}
